import React, { useMemo } from 'react';
import { View, Text, StyleSheet, ViewStyle } from 'react-native';
import { formatCurrency } from '@/utils/common/formatting';
import { useDesktopColors } from '@/constants/residents/desktop/color';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import type { ThemeContextValue } from '@/hooks/residents/useThemeColors';
import { PAYMENT_TYPOGRAPHY } from '@/constants/residents/mobile/paymentDesignTokens';
import type { BalanceItem } from '@/types/residents/lease';

type Variant = 'desktop' | 'mobile' | 'marketplace' | 'mobileLight';

interface ChargeBreakdownProps {
  balanceItems: BalanceItem[];
  balanceDue: number;
  accountCredit?: number;
  variant?: Variant;
  style?: ViewStyle;
}

export const ChargeBreakdown: React.FC<ChargeBreakdownProps> = ({
  balanceItems,
  balanceDue,
  accountCredit = 0,
  variant = 'desktop',
  style,
}) => {
  const DESKTOP_COLORS = useDesktopColors();
  const theme = useThemeColors();
  const desktopStyles = useMemo(() => buildDesktopStyles(DESKTOP_COLORS, theme.colors.activityIcon.bg), [DESKTOP_COLORS, theme.colors.activityIcon.bg]);
  const mobileStyles = useMemo(() => buildMobileStyles(theme), [theme.mode]);
  const marketplaceStyles = useMemo(() => buildMarketplaceStyles(theme), [theme.mode]);
  const mobileLightStyles = useMemo(() => buildMobileLightStyles(theme), [theme.mode]);

  const hasItems = balanceItems && balanceItems.length > 0 && balanceItems.some(item => item.balanceDue !== 0);
  const hasCredit = accountCredit > 0;
  if (!hasItems && !hasCredit) return null;

  const variantStyles = {
    desktop: desktopStyles,
    mobile: mobileStyles,
    marketplace: marketplaceStyles,
    mobileLight: mobileLightStyles,
  } as const;
  const s = variantStyles[variant];
  const isCredit = balanceDue < 0;
  const totalLabel = isCredit ? 'Account credit' : 'Total due';

  return (
    <View style={[s.section, style]}>
      <Text style={s.title}>{isCredit ? 'Credits' : 'Charges'}</Text>
      {balanceItems.map((item, index) => (
        <View key={`${item.chargeCode ?? item.description}-${index}`} style={s.row}>
          <Text style={s.description} numberOfLines={1}>
            {item.description}
          </Text>
          <Text style={s.amount}>{formatCurrency(item.balanceDue)}</Text>
        </View>
      ))}
      <View style={s.totalRow}>
        <Text style={s.totalLabel}>{totalLabel}</Text>
        <Text style={[s.totalAmount, isCredit && s.creditAmount]}>{formatCurrency(balanceDue)}</Text>
      </View>
      {accountCredit > 0 && !isCredit && (
        <View style={s.totalRow}>
          <Text style={s.totalLabel}>Account credit</Text>
          <Text style={[s.totalAmount, s.creditAmount]}>{formatCurrency(accountCredit)}</Text>
        </View>
      )}
    </View>
  );
};

type DesktopColors = ReturnType<typeof useDesktopColors>;

const buildDesktopStyles = (DESKTOP_COLORS: DesktopColors, sectionBg: string) => StyleSheet.create({
  section: {
    backgroundColor: sectionBg,
    borderRadius: 10,
    padding: 14,
    gap: 8,
  },
  title: {
    fontSize: 13,
    fontWeight: '600',
    color: DESKTOP_COLORS.TEXT_PRIMARY,
    marginBottom: 2,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  description: {
    fontSize: 13,
    color: DESKTOP_COLORS.TEXT_TERTIARY,
    flex: 1,
    marginRight: 12,
  },
  amount: {
    fontSize: 13,
    fontWeight: '500',
    color: DESKTOP_COLORS.TEXT_PRIMARY,
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderTopWidth: 1,
    borderTopColor: DESKTOP_COLORS.BORDER_SECONDARY,
    paddingTop: 8,
    marginTop: 2,
  },
  totalLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: DESKTOP_COLORS.TEXT_PRIMARY,
  },
  totalAmount: {
    fontSize: 13,
    fontWeight: '600',
    color: DESKTOP_COLORS.TEXT_PRIMARY,
  },
  creditAmount: {
    color: DESKTOP_COLORS.TEXT_SUCCESS,
  },
});

const buildMobileStyles = (theme: ThemeContextValue) => StyleSheet.create({
  section: {
    backgroundColor: theme.colors.glass.buttonBg,
    borderRadius: 10,
    padding: 12,
    gap: 8,
  },
  title: {
    ...PAYMENT_TYPOGRAPHY.textXsMedium,
    color: theme.colors.text.primary,
    marginBottom: 2,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  description: {
    ...PAYMENT_TYPOGRAPHY.bodySm,
    color: theme.colors.text.muted,
    flex: 1,
    marginRight: 12,
  },
  amount: {
    ...PAYMENT_TYPOGRAPHY.bodySmMedium,
    color: theme.colors.text.primary,
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderTopWidth: 1,
    borderTopColor: theme.semantic.border.secondary,
    paddingTop: 8,
    marginTop: 2,
  },
  totalLabel: {
    ...PAYMENT_TYPOGRAPHY.textXsMedium,
    color: theme.colors.text.primary,
  },
  totalAmount: {
    ...PAYMENT_TYPOGRAPHY.textXsMedium,
    color: theme.colors.text.primary,
  },
  creditAmount: {
    color: theme.semantic.status.success,
  },
});

const buildMarketplaceStyles = (theme: ThemeContextValue) => StyleSheet.create({
  section: {
    backgroundColor: theme.semantic.background.tertiary,
    borderRadius: 12,
    padding: 16,
    gap: 10,
  },
  title: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.semantic.text.primary,
    marginBottom: 4,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  description: {
    fontSize: 14,
    fontWeight: '400',
    color: theme.semantic.text.secondary,
    flex: 1,
    marginRight: 12,
  },
  amount: {
    fontSize: 14,
    fontWeight: '500',
    color: theme.semantic.text.primary,
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderTopWidth: 1,
    borderTopColor: theme.semantic.border.input,
    paddingTop: 10,
    marginTop: 2,
  },
  totalLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.semantic.text.primary,
  },
  totalAmount: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.semantic.text.primary,
  },
  creditAmount: {
    color: theme.semantic.status.success,
  },
});

const buildMobileLightStyles = (theme: ThemeContextValue) => StyleSheet.create({
  section: {
    backgroundColor: theme.semantic.background.primary,
    borderRadius: 10,
    padding: 12,
    gap: 8,
  },
  title: {
    fontSize: 13,
    fontWeight: '600',
    color: theme.semantic.text.primary,
    marginBottom: 2,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  description: {
    fontSize: 13,
    fontWeight: '400',
    color: theme.semantic.text.tertiary,
    flex: 1,
    marginRight: 12,
  },
  amount: {
    fontSize: 13,
    fontWeight: '500',
    color: theme.semantic.text.primary,
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderTopWidth: 1,
    borderTopColor: theme.semantic.border.secondary,
    paddingTop: 8,
    marginTop: 2,
  },
  totalLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: theme.semantic.text.primary,
  },
  totalAmount: {
    fontSize: 13,
    fontWeight: '600',
    color: theme.semantic.text.primary,
  },
  creditAmount: {
    color: theme.semantic.status.success,
  },
});